import type { Component } from 'vue'
import {
  Activity,
  BookOpen,
  ClipboardCheck,
  FilePlus,
  FoldVertical,
  Info,
  MessageSquarePlus,
  Pencil,
  ScrollText,
  Search,
} from 'lucide-vue-next'

export type SlashCommandContext = {
  hasYaml: boolean
  editorKind?: 'task' | 'dashboard'
}

export type SlashCommandArgMode = 'none' | 'optional' | 'required'

type SlashCommandBase = {
  /** Command name without the leading slash, e.g. "summarize". */
  name: string
  description: string
  icon: Component
  argMode: SlashCommandArgMode
  /** Hint shown after the name in the menu, e.g. "<description>". */
  argHint?: string
  /** Alternate intent words used by menu search. */
  keywords?: string[]
}

export type SlashCommand = SlashCommandBase &
  (
    | { kind: 'prompt'; build: (args: string, ctx: SlashCommandContext) => string }
    | { kind: 'action'; action: 'new' | 'rename' | 'compact' }
  )

/**
 * Slash commands available in the AI composer. "prompt" commands expand the
 * typed input into a concise instruction that is sent (and persisted) as a
 * normal message; "action" commands run locally without hitting the model.
 */
export const SLASH_COMMANDS: SlashCommand[] = [
  {
    name: 'summarize',
    description: 'Recap this conversation — findings, decisions, next steps',
    icon: ScrollText,
    argMode: 'optional',
    argHint: '[focus]',
    kind: 'prompt',
    build: args =>
      args
        ? `Summarize our conversation so far, focusing on: ${args}`
        : 'Summarize our conversation so far — the key findings, any decisions made, and the outstanding next steps.',
  },
  {
    name: 'explain',
    description: "Explain what I'm currently looking at",
    icon: Info,
    argMode: 'optional',
    argHint: '[focus]',
    kind: 'prompt',
    build: args =>
      `Explain what I'm currently looking at on this page${
        args ? `, focusing on: ${args}` : ''
      }. Walk me through the key details and what I can do here.`,
  },
  {
    name: 'diagnose',
    description: 'Investigate a failure and suggest a fix',
    icon: Activity,
    argMode: 'optional',
    argHint: '[what failed]',
    keywords: ['error', 'debug', 'broken'],
    kind: 'prompt',
    build: args =>
      `Investigate the failure or problem indicated by the current page${
        args ? ` — specifically: ${args}` : ''
      }. If this is a run or job run, pull the relevant run details and task runs. Find the root cause from available errors and tell me how to fix it; if there is no identifiable failure context, ask what should be diagnosed.`,
  },
  {
    name: 'task',
    description: 'Scaffold a new task from a description',
    icon: FilePlus,
    argMode: 'required',
    argHint: '<description>',
    kind: 'prompt',
    build: args => `Create a new Envoy task that does the following: ${args}`,
  },
  {
    name: 'find',
    description: 'Search tasks, jobs, templates, connectors, and triggers',
    icon: Search,
    argMode: 'required',
    argHint: '<query>',
    keywords: ['locate', 'lookup'],
    kind: 'prompt',
    build: args => `Search the app for: ${args}`,
  },
  {
    name: 'docs',
    description: 'Explain a step, connector, or concept from the docs',
    icon: BookOpen,
    argMode: 'required',
    argHint: '<topic>',
    keywords: ['reference', 'documentation'],
    kind: 'prompt',
    build: args => `Using the Envoy reference docs, explain: ${args}`,
  },
  {
    name: 'review',
    description: 'Review the current task or dashboard YAML for issues',
    icon: ClipboardCheck,
    argMode: 'optional',
    argHint: '[focus]',
    keywords: ['audit', 'check', 'validate'],
    kind: 'prompt',
    build: (args, ctx) => {
      const target = ctx.editorKind
        ? `current ${ctx.editorKind} YAML`
        : 'relevant YAML or configuration on the current page'
      const focus = args ? ` Focus on: ${args}.` : ''
      const readFirst = ctx.hasYaml ? '' : ' Read it first.'
      return `Review the ${target} for correctness, performance, and best practices.${focus}${readFirst} Point out any issues and suggest concrete improvements.`
    },
  },
  {
    name: 'new',
    description: 'Start a fresh conversation',
    icon: MessageSquarePlus,
    argMode: 'none',
    kind: 'action',
    action: 'new',
  },
  {
    name: 'rename',
    description: 'Rename this conversation',
    icon: Pencil,
    argMode: 'required',
    argHint: '<new title>',
    kind: 'action',
    action: 'rename',
  },
  {
    name: 'compact',
    description: 'Summarize earlier messages to free up context',
    icon: FoldVertical,
    argMode: 'none',
    keywords: ['context', 'shorten'],
    kind: 'action',
    action: 'compact',
  },
]

const BARE_NAME_RE = /^\/([a-z]*)$/i
const FULL_COMMAND_RE = /^\/([a-z]+)(?:\s+([\s\S]*))?$/i

/**
 * When the input is a bare "/word" (no whitespace yet), return the lowercased
 * partial name used to filter the menu. Returns null when the menu should be
 * closed (no leading slash, or args already typed).
 */
export function slashMenuQuery(input: string): string | null {
  const m = BARE_NAME_RE.exec(input)
  return m ? m[1].toLowerCase() : null
}

export function filterCommands(query: string): SlashCommand[] {
  const normalized = query.trim().toLowerCase()
  if (!normalized) return SLASH_COMMANDS
  return SLASH_COMMANDS.filter(command =>
    [command.name, command.description, command.argHint, ...(command.keywords ?? [])].some(value =>
      value?.toLowerCase().includes(normalized)
    )
  )
}

export type SlashCommandInput =
  | { kind: 'command'; command: SlashCommand; args: string }
  | { kind: 'unknown'; name: string }
  | { kind: 'none' }

/**
 * Distinguish recognized commands, unknown command-shaped input, and prose.
 * Paths such as "/api/tasks" remain prose because they are not command-shaped.
 */
export function classifySlashCommandInput(input: string): SlashCommandInput {
  const match = FULL_COMMAND_RE.exec(input.trim())
  if (!match) return { kind: 'none' }

  const name = match[1].toLowerCase()
  const command = SLASH_COMMANDS.find(candidate => candidate.name === name)
  if (!command) return { kind: 'unknown', name }
  return { kind: 'command', command, args: (match[2] ?? '').trim() }
}

/**
 * Parse a recognized leading slash command (and its trailing args) from sent
 * input. Use classifySlashCommandInput at dispatch sites that must distinguish
 * unknown slash-like commands from normal prose.
 */
export function parseSlashCommand(input: string): { command: SlashCommand; args: string } | null {
  const parsed = classifySlashCommandInput(input)
  if (parsed.kind !== 'command') return null
  return { command: parsed.command, args: parsed.args }
}

export function slashCommandUsage(command: SlashCommand): string {
  return `Usage: /${command.name}${command.argHint ? ` ${command.argHint}` : ''}`
}

export function validateSlashCommandArgs(command: SlashCommand, args: string): string | null {
  if (command.argMode === 'required' && !args.trim()) {
    return slashCommandUsage(command)
  }
  if (command.argMode === 'none' && args.trim()) {
    return `/${command.name} does not accept arguments. ${slashCommandUsage(command)}`
  }
  return null
}

export function executesOnMenuSelect(command: SlashCommand): boolean {
  return command.kind === 'action' && command.argMode === 'none'
}
